import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Button } from 'flowbite-react';
import { PlusIcon } from 'lucide-react';
import { useCallback } from 'react';
import { Link, useNavigate } from 'react-router';

import { useApi } from '../../../api/useApi.hook';
import { ErrorSwitch } from '../../../components/errorMessage/errorSwitch.component';
import { pathWithParams } from '../../../lib/routing.utils';
import { AppHeader } from '../../components/appHeader/appHeader.component';
import { AppHeaderButton } from '../../components/appHeader/appHeaderButton.component';
import DocumentCard from '../../components/documentCard/documentCard.component';
import { RouteIndex } from '../../routeIndex';

const DashboardView = () => {
    const api = useApi();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    
    const myDocuments = useQuery({
        queryKey: ['my-documents'],
        queryFn: async () => {
            return api.v1.documents.getMy();
        }
    });

    const sharedDocuments = useQuery({
        queryKey: ['shared-documents'],
        queryFn: async () => {
            return api.v1.documents.getSharedWithMeDocumentList();
        }
    });

    const reload = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['my-documents'] });
        queryClient.invalidateQueries({ queryKey: ['shared-documents'] });
    }, []);

    const isLoading = myDocuments.isLoading || sharedDocuments.isLoading;
    const totalCount = (myDocuments.data?.count ?? 0) + (sharedDocuments.data?.count ?? 0);

    return (
        <>
            <AppHeader
                pageTitle='Mé dokumenty'
            >
                <AppHeaderButton
                    style='primary'
                    size='lg'
                    onClick={() => navigate(pathWithParams(RouteIndex.newDocument, {}))}
                >
                    <PlusIcon size={20} />
                    <span className='hidden lg:block'>Nový dokument</span>
                </AppHeaderButton>
            </AppHeader>

            {isLoading && (
                <div className='p-4 pt-16 md:pt-20 flex flex-col items-center'>
                    <div className={clsx(
                        'w-full sm:w-auto grid gap-4 lg:gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
                    )}>
                        {Array.from({ length: 3 }).map((_, index) => (
                            <div key={index} className="flex w-full sm:w-[20rem] h-96 mb-4 p-0 bg-gray-200 animate-pulse rounded-md"></div>
                        ))}
                    </div>
                </div>
            )}

            {(myDocuments.error || sharedDocuments.error) && (
                <div className='p-4 pt-16 md:pt-20 flex flex-col items-center'>
                    <ErrorSwitch
                        error={myDocuments.error || sharedDocuments.error}
                        default={{ message: 'Při načítání dat došlo k chybě. Zkuste to prosím za chvíli znovu.', showDetails: true }}
                        options={[]}
                        retry={reload}
                    />
                </div>
            )}

            {!isLoading && totalCount > 0 && (
                <div className='p-4 pt-16 md:pt-20 md:pb-12 flex flex-col items-center'>
                    {!isLoading && myDocuments.data && myDocuments.data.count > 0 && (
                        <div className={clsx(
                            'w-full sm:w-auto grid gap-4 lg:gap-8',
                            myDocuments.data.items.length === 1 && 'grid-cols-1',
                            myDocuments.data.items.length === 2 && 'grid-cols-1 sm:grid-cols-2',
                            myDocuments.data.items.length === 3 && 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
                            myDocuments.data.items.length >= 4 && 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                        )}>
                            {myDocuments.data.items.map((document) => (
                                <DocumentCard
                                    key={document.documentId}
                                    document={document}
                                    className='w-full sm:w-[20rem]'
                                />
                            ))}
                        </div>
                    )}

                    {!isLoading && sharedDocuments.data && sharedDocuments.data.count > 0 && (
                        <>
                            <h2 className='text-lg font-medium mt-8 mb-6'>Dokumenty sdílené se mnou</h2>

                            <div className={clsx(
                                'grid gap-4 lg:gap-8',
                                sharedDocuments.data.items.length === 1 && 'grid-cols-1',
                                sharedDocuments.data.items.length === 2 && 'grid-cols-1 sm:grid-cols-2',
                                sharedDocuments.data.items.length === 3 && 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3',
                                sharedDocuments.data.items.length >= 4 && 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
                            )}>
                                {sharedDocuments.data.items.map((document) => (
                                    <DocumentCard
                                        key={document.documentId}
                                        document={document}
                                        className='w-full sm:w-[20rem]'
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}

            {!isLoading && totalCount === 0 && (
                <div className="flex flex-col justify-center items-center min-h-screen">
                    <div className="flex flex-col gap-4 items-center mb-16">
                        <img
                            src='/assets/new-doc-illustration.png'
                            alt='Nový dokument - ilustrace'
                            width={657}
                            height={1027}
                            className='w-[10rem] h-auto opacity-10 shrink-0 mb-4'
                        />
                        <Button
                            size='lg'
                            color='primary'
                            as={Link}
                            to={RouteIndex.newDocument}
                        >
                            Vytvořte svůj první dokument
                        </Button>
                        {/* <p className='text-sm text-gray-700 text-center'>Lorem ipsum dolor sit amet.</p> */}
                    </div>
                </div>
            ) }

        </>
    );
};

export default DashboardView;
